/**
 * Oath Large Bond Service
 * API calls for large bond operations using Oath authentication
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Parse API response
 */
const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  return data.result !== undefined ? data.result : data;
};

// ============================================
// LARGE BOND ENDPOINTS
// ============================================

/**
 * Get all large bonds for the current user
 */
export const getLargeBonds = async (agencyId) => {
  try {
    let url = `${BASE_URL}oath/largebond/bonds`;
    if (agencyId) url += `?agencyId=${agencyId}`;
    const response = await authFetch(url);
    if (!response.ok) {
      return { success: false, error: `Failed to fetch bonds: ${response.statusText}` };
    }
    const result = await parseResponse(response);
    return { success: true, data: Array.isArray(result) ? result : [] };
  } catch (error) {
    return { success: false, error: error.message || 'Error fetching bonds' };
  }
};

/**
 * Get full details for a single large bond
 */
export const getLargeBondDetails = async (id) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/largebond/${id}/details`);
    if (!response.ok) {
      return { success: false, error: `Failed to fetch bond details: ${response.statusText}` };
    }
    const result = await parseResponse(response);
    return { success: true, data: result };
  } catch (error) {
    return { success: false, error: error.message || 'Error fetching bond details' };
  }
};

// The API's validation failures (400) come back as { success: false, message }.
// Pull the message out so the form can show it verbatim instead of a JSON blob;
// fall back to the raw text for anything that isn't that shape.
const readErrorMessage = async (response, fallback) => {
  const text = await response.text().catch(() => '');
  if (!text) return fallback;
  try {
    const parsed = JSON.parse(text);
    return parsed?.message || parsed?.detail || parsed?.title || text;
  } catch {
    return text;
  }
};

/**
 * Create a new large bond (requires agencyId query param)
 */
export const createLargeBond = async (agencyId, payload) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/largebond/bonds?agencyId=${agencyId}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      return { success: false, error: await readErrorMessage(response, `Failed to create bond: ${response.statusText}`) };
    }
    const result = await parseResponse(response);
    return { success: true, data: result };
  } catch (error) {
    return { success: false, error: error.message || 'Error creating bond' };
  }
};

/**
 * Update an existing large bond (requires agencyId query param)
 */
export const updateLargeBond = async (id, agencyId, payload) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/largebond/${id}?agencyId=${agencyId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      return { success: false, error: await readErrorMessage(response, `Failed to update bond: ${response.statusText}`) };
    }
    const result = await parseResponse(response);
    return { success: true, data: result };
  } catch (error) {
    return { success: false, error: error.message || 'Error updating bond' };
  }
};

/**
 * Cancel a large bond
 */
export const cancelLargeBond = async (id) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/largebond/${id}/cancel`, {
      method: 'POST',
    });
    if (!response.ok) {
      return { success: false, error: `Failed to cancel bond: ${response.statusText}` };
    }
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || 'Error cancelling bond' };
  }
};

/**
 * Add power info to an approved large bond.
 * Returns { success, power: { largeBondCourtInfoId, powerNumber, executionAmount } }
 */
export const addPowerInfo = async (id, powerData) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/largebond/${id}/power`, {
      method: 'POST',
      body: JSON.stringify(powerData),
    });
    if (!response.ok) {
      const errorData = await response.text().catch(() => '');
      return { success: false, error: errorData || `Failed to save power info: ${response.statusText}` };
    }
    const result = await parseResponse(response);
    return { success: true, power: result.power || result };
  } catch (error) {
    return { success: false, error: error.message || 'Error saving power info' };
  }
};

/**
 * Delete a power from an approved large bond by court info ID.
 */
export const deletePowerFromBond = async (bondId, powerId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/largebond/${bondId}/power/${powerId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errorData = await response.text().catch(() => '');
      return { success: false, error: errorData || `Failed to delete power: ${response.statusText}` };
    }
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || 'Error deleting power' };
  }
};

/**
 * Upload power proof documents
 * Sends all files in a single request via multipart/form-data.
 * Do NOT set Content-Type header — browser sets it with boundary for FormData.
 */
export const uploadPowerProof = async (id, files, powerNumber) => {
  try {
    const formData = new FormData();
    for (const file of Array.isArray(files) ? files : [files]) {
      formData.append('file', file);
    }

    let url = `${BASE_URL}oath/largebond/${id}/upload-power-proof`;
    if (powerNumber) {
      url += `?powerNumber=${encodeURIComponent(powerNumber)}`;
    }

    const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
    let response = await fetch(url, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });

    // Auto-refresh on 401
    if (response.status === 401) {
      const refreshResult = await oathRefreshToken();
      if (refreshResult.success) {
        const newToken = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
        response = await fetch(url, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${newToken}` },
          body: formData,
        });
      }
    }

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return { success: false, error: data.message || 'Upload failed' };
    }
    return { success: true, message: data.message };
  } catch (error) {
    return { success: false, error: error.message || 'Error uploading files' };
  }
};
